import React, { useState, useEffect } from 'react';
import { useAuth } from '../../AuthContext';
import MainStructure from '../../components/general/MainStructure';
import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';
import { Modal, Button } from 'react-bootstrap';
import { toast } from 'react-toastify';
import * as Icon from 'react-feather';
import ImageUploader from '../ImageUploader';
import VideoUploader from '../VideoUploader';
import AudioUploader from '../AudioUploader';

// ── Inline-editable title ──────────────────────────────────────────────────────
function EditableTitle({ item, onSave }) {
    const [isEditing, setIsEditing] = useState(false);
    const [title, setTitle] = useState(item.title || '');

    const commit = () => {
        setIsEditing(false);
        if (title !== item.title) onSave(item.id, title);
    };

    if (isEditing) {
        return (
            <input
                autoFocus
                className="form-control form-control-sm"
                style={{ fontSize: '0.8rem', padding: '2px 6px', height: 26 }}
                value={title}
                onChange={e => setTitle(e.target.value)}
                onBlur={commit}
                onKeyDown={e => e.key === 'Enter' && commit()}
            />
        );
    }
    return (
        <span
            onClick={() => setIsEditing(true)}
            title="Click to rename"
            style={{ fontSize: '0.8rem', fontWeight: 600, color: '#334155', cursor: 'text', display: 'flex', alignItems: 'center', gap: 4 }}
            className="text-truncate"
        >
            {title || <span style={{ color: '#94a3b8', fontWeight: 400 }}>Untitled</span>}
            <Icon.Edit2 size={10} color="#b0bec5" style={{ flexShrink: 0 }} />
        </span>
    );
}

// ── Main component ─────────────────────────────────────────────────────────────
function MediaLibrary() {
    const [media, setMedia]                         = useState([]);
    const [filter, setFilter]                       = useState('images');
    const [loading, setLoading]                     = useState(false);
    const [page, setPage]                           = useState(1);
    const [hasMore, setHasMore]                     = useState(true);
    const [viewType, setViewType]                   = useState('grid');
    const [showUploadModal, setShowUploadModal]     = useState(false);
    const [tempImages, setTempImages]               = useState([]);
    const [tempVideos, setTempVideos]               = useState([]);
    const [tempAudio, setTempAudio]                 = useState([]);
    const [showDeleteModal, setShowDeleteModal]     = useState(false);
    const [showViewModal, setShowViewModal]         = useState(false);
    const [selectedItem, setSelectedItem]           = useState(null);

    const fetchMedia = async (pageNum, isNewFilter = false) => {
        setLoading(true);
        try {
            // Audio lives in its own table/endpoint; images & videos share getMediaLibrary.
            const url = filter === 'audio'
                ? `https://api.schedpilot.com/wp-json/api/v1/getAudioLibrary?page=${pageNum}`
                : `https://api.schedpilot.com/wp-json/api/v1/getMediaLibrary?type=${filter}&page=${pageNum}`;
            const res  = await fetch(url, { credentials: 'include' });
            const json = await res.json();
            if (json.code === 200) {
                setMedia(prev => isNewFilter ? json.data : [...prev, ...json.data]);
                setHasMore(!!json.hasMore);
            }
        } catch {
            toast.error('Error loading media');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { setPage(1); fetchMedia(1, true); }, [filter]);

    const handleUpdateTitle = async (id, newTitle) => {
        try {
            const res  = await fetch('https://api.schedpilot.com/wp-json/api/v1/updateMediaTitle', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ id, title: newTitle, type: filter }),
            });
            const json = await res.json();
            if (json.code === 200) {
                setMedia(m => m.map(i => i.id === id ? { ...i, title: newTitle } : i));
                toast.success('Title updated');
            }
        } catch { toast.error('Failed to update title'); }
    };

    const handleDelete = async () => {
        try {
            await fetch(
                `https://api.schedpilot.com/wp-json/api/v1/deleteMediaItem?id=${selectedItem.id}&type=${filter}`,
                { method: 'POST', credentials: 'include' }
            );
            setMedia(m => m.filter(i => i.id !== selectedItem.id));
            toast.success('Deleted');
        } catch { toast.error('Delete failed'); }
        finally { setShowDeleteModal(false); }
    };

    const handleUploadFinished = () => {
        setShowUploadModal(false);
        setTempImages([]);
        setTempVideos([]);
        setTempAudio([]);
        setPage(1);
        fetchMedia(1, true);
        toast.success('Library updated!');
    };

    const fmt = dateStr => {
        if (!dateStr) return '';
        return new Date(dateStr).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
    };

    const openViewer = item => { setSelectedItem(item); setShowViewModal(true); };

    // ── Toolbar button styles ────────────────────────────────────────────────
    const filterBtn = active => ({
        padding: '5px 16px', fontSize: '0.8rem', fontWeight: active ? 600 : 400,
        borderRadius: 20, border: '1px solid', cursor: 'pointer',
        borderColor: active ? '#0f172a' : '#d1d9e0',
        background:  active ? '#0f172a' : '#fff',
        color:       active ? '#fff'    : '#64748b',
        transition: 'all 0.15s',
    });

    const viewBtn = active => ({
        padding: '5px 8px', fontSize: '0.78rem', borderRadius: 7, border: '1px solid',
        cursor: 'pointer',
        borderColor: active ? '#4f46e5' : '#d1d9e0',
        background:  active ? '#eff6ff' : '#fff',
        color:       active ? '#4f46e5' : '#64748b',
        display: 'flex', alignItems: 'center',
    });

    return (
        <MainStructure activeItem="media-library">
            <div className="container-fluid px-0">

                {/* ── Header ─────────────────────────────────────────────── */}
                <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4">
                    <div>
                        <h1 style={{ fontSize: '1.3rem', fontWeight: 500, color: '#0f172a', margin: 0 }}>Media Library</h1>
                        <p style={{ fontSize: '0.85rem', color: '#7d8fa3', margin: 0 }}>Your uploaded photos, videos and audio</p>
                    </div>
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                        {/* Filter pills */}
                        <div className="d-flex gap-1">
                            <button style={filterBtn(filter === 'images')} onClick={() => setFilter('images')}>
                                <Icon.Image size={13} style={{ marginRight: 5, verticalAlign: 'middle' }} />Images
                            </button>
                            <button style={filterBtn(filter === 'videos')} onClick={() => setFilter('videos')}>
                                <Icon.Film size={13} style={{ marginRight: 5, verticalAlign: 'middle' }} />Videos
                            </button>
                            <button style={filterBtn(filter === 'audio')} onClick={() => setFilter('audio')}>
                                <Icon.Music size={13} style={{ marginRight: 5, verticalAlign: 'middle' }} />Audio
                            </button>
                        </div>
                        {/* View toggle */}
                        <div className="d-flex gap-1">
                            <button style={viewBtn(viewType === 'grid')} onClick={() => setViewType('grid')}>
                                <Icon.Grid size={14} />
                            </button>
                            <button style={viewBtn(viewType === 'list')} onClick={() => setViewType('list')}>
                                <Icon.List size={14} />
                            </button>
                        </div>
                        {/* Upload */}
                        <button
                            onClick={() => setShowUploadModal(true)}
                            style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#2fba01', border: 'none', borderRadius: 8, padding: '7px 16px', color: '#fff', fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer' }}
                        >
                            <Icon.Upload size={14} /> Upload
                        </button>
                    </div>
                </div>

                {/* ── Grid view ──────────────────────────────────────────── */}
                {viewType === 'grid' && (
                    <div className="row g-3">
                        {media.map(item => (
                            <div key={item.id} className="col-6 col-sm-4 col-md-3 col-xl-2">
                                <div style={{ background: '#fff', border: '1px solid #b0bec5', borderRadius: 12, overflow: 'hidden', display: 'flex', flexDirection: 'column', height: '100%' }}>
                                    {/* Thumbnail */}
                                    <div
                                        onClick={() => openViewer(item)}
                                        style={{ aspectRatio: '1/1', cursor: 'pointer', background: '#0f172a', position: 'relative', overflow: 'hidden' }}
                                    >
                                        {filter === 'images' ? (
                                            <img
                                                src={item.file_url}
                                                alt={item.title}
                                                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                                            />
                                        ) : filter === 'audio' ? (
                                            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg,#4f46e5,#7c3aed)' }}>
                                                <Icon.Music size={40} color="rgba(255,255,255,0.92)" />
                                            </div>
                                        ) : (
                                            <>
                                                <video
                                                    src={`${item.file_url}#t=0.1`}
                                                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                                                    preload="metadata"
                                                />
                                                <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.3)' }}>
                                                    <div style={{ width: 34, height: 34, borderRadius: '50%', background: 'rgba(255,255,255,0.9)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                        <Icon.Play size={14} color="#0f172a" fill="#0f172a" style={{ marginLeft: 2 }} />
                                                    </div>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                    {/* Meta */}
                                    <div style={{ padding: '10px 10px 8px', flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
                                        <EditableTitle item={item} onSave={handleUpdateTitle} />
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                            <span style={{ fontSize: '0.7rem', color: '#94a3b8' }}>{fmt(item.created_at || item.date)}</span>
                                            <div style={{ display: 'flex', gap: 2 }}>
                                                <button
                                                    onClick={() => openViewer(item)}
                                                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px', color: '#64748b', borderRadius: 4 }}
                                                    title="Preview"
                                                >
                                                    <Icon.Maximize size={13} />
                                                </button>
                                                <button
                                                    onClick={e => { e.stopPropagation(); setSelectedItem(item); setShowDeleteModal(true); }}
                                                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px', color: '#ef4444', borderRadius: 4 }}
                                                    title="Delete"
                                                >
                                                    <Icon.Trash2 size={13} />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* ── List view ──────────────────────────────────────────── */}
                {viewType === 'list' && (
                    <div style={{ background: '#fff', border: '1px solid #b0bec5', borderRadius: 12, overflow: 'hidden' }}>
                        <table className="table align-middle mb-0" style={{ fontSize: '0.85rem' }}>
                            <thead>
                                <tr style={{ background: '#f8fafc' }}>
                                    <th style={{ width: 64, padding: '10px 16px', fontSize: '0.72rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#64748b', border: 'none' }}>Preview</th>
                                    <th style={{ padding: '10px 16px', fontSize: '0.72rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#64748b', border: 'none' }}>Name</th>
                                    <th style={{ padding: '10px 16px', fontSize: '0.72rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#64748b', border: 'none' }}>Added</th>
                                    <th style={{ padding: '10px 16px', fontSize: '0.72rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#64748b', border: 'none', textAlign: 'right' }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {media.map((item, i) => (
                                    <tr key={item.id} style={{ borderTop: i === 0 ? '1px solid #f1f5f9' : '1px solid #f1f5f9' }}>
                                        <td style={{ padding: '10px 16px' }}>
                                            <div
                                                onClick={() => openViewer(item)}
                                                style={{ width: 48, height: 48, borderRadius: 8, overflow: 'hidden', background: '#0f172a', cursor: 'pointer', position: 'relative' }}
                                            >
                                                {filter === 'images' ? (
                                                    <img src={item.file_url} style={{ width: '100%', height: '100%', objectFit: 'cover' }} alt="" />
                                                ) : filter === 'audio' ? (
                                                    <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg,#4f46e5,#7c3aed)' }}>
                                                        <Icon.Music size={20} color="rgba(255,255,255,0.92)" />
                                                    </div>
                                                ) : (
                                                    <video src={`${item.file_url}#t=0.1`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} preload="metadata" />
                                                )}
                                            </div>
                                        </td>
                                        <td style={{ padding: '10px 16px', maxWidth: 320 }}>
                                            <EditableTitle item={item} onSave={handleUpdateTitle} />
                                        </td>
                                        <td style={{ padding: '10px 16px', color: '#64748b', whiteSpace: 'nowrap' }}>
                                            {fmt(item.created_at || item.date)}
                                        </td>
                                        <td style={{ padding: '10px 16px', textAlign: 'right' }}>
                                            <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                                                <button
                                                    onClick={() => openViewer(item)}
                                                    className="btn btn-sm sp-btn-ghost"
                                                    style={{ border: '1px solid #d1d9e0', borderRadius: 7, color: '#475569', background: '#fff', fontSize: '0.78rem' }}
                                                >
                                                    <Icon.Eye size={13} />
                                                </button>
                                                <button
                                                    onClick={() => { setSelectedItem(item); setShowDeleteModal(true); }}
                                                    className="btn btn-sm sp-btn-danger"
                                                    style={{ border: '1px solid #fecaca', borderRadius: 7, color: '#dc2626', background: '#fff', fontSize: '0.78rem' }}
                                                >
                                                    <Icon.Trash2 size={13} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* ── Empty state ─────────────────────────────────────────── */}
                {media.length === 0 && !loading && (
                    <div style={{ background: '#f8fafc', border: '1px dashed #d1d9e0', borderRadius: 12, padding: '56px 24px', textAlign: 'center', marginTop: 8 }}>
                        {filter === 'images' ? <Icon.Image size={36} color="#d1d9e0" /> : filter === 'audio' ? <Icon.Music size={36} color="#d1d9e0" /> : <Icon.Film size={36} color="#d1d9e0" />}
                        <p style={{ color: '#94a3b8', fontSize: '0.9rem', margin: '12px 0 0' }}>
                            No {filter} uploaded yet.{' '}
                            <span
                                style={{ color: '#2fba01', cursor: 'pointer', textDecoration: 'underline' }}
                                onClick={() => setShowUploadModal(true)}
                            >
                                Upload now
                            </span>
                        </p>
                    </div>
                )}

                {/* ── Load more ───────────────────────────────────────────── */}
                {hasMore && media.length > 0 && (
                    <div className="text-center mt-4 mb-2">
                        <button
                            disabled={loading}
                            onClick={() => { const next = page + 1; setPage(next); fetchMedia(next); }}
                            className="btn btn-sm sp-btn-ghost"
                            style={{ border: '1px solid #d1d9e0', borderRadius: 8, color: '#475569', background: '#fff', padding: '8px 28px', fontSize: '0.85rem' }}
                        >
                            {loading ? 'Loading…' : 'Load more'}
                        </button>
                    </div>
                )}
            </div>

            {/* ── Preview modal ───────────────────────────────────────────── */}
            <Modal show={showViewModal} onHide={() => setShowViewModal(false)} size="lg" centered>
                <Modal.Header closeButton style={{ border: 'none', paddingBottom: 0 }} />
                <Modal.Body className="text-center p-4">
                    {selectedItem && (filter === 'images' ? (
                        <img src={selectedItem.file_url} className="img-fluid rounded" style={{ maxHeight: '80vh' }} alt="Full view" />
                    ) : filter === 'audio' ? (
                        <div className="p-4">
                            <div style={{ width: 90, height: 90, borderRadius: '50%', margin: '0 auto 18px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg,#4f46e5,#7c3aed)' }}>
                                <Icon.Music size={40} color="#fff" />
                            </div>
                            <div style={{ fontWeight: 600, color: '#0f172a', marginBottom: 14 }}>{selectedItem.title || 'Audio track'}</div>
                            <audio src={selectedItem.file_url} controls autoPlay className="w-100" />
                        </div>
                    ) : (
                        <video src={selectedItem.file_url} controls autoPlay className="w-100 rounded" style={{ maxHeight: '80vh' }} />
                    ))}
                </Modal.Body>
            </Modal>

            {/* ── Delete modal ────────────────────────────────────────────── */}
            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
                <Modal.Header closeButton style={{ border: 'none', paddingBottom: 0 }}>
                    <Modal.Title style={{ fontSize: '1rem', fontWeight: 500 }}>Delete asset?</Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ fontSize: '0.875rem', color: '#64748b' }}>
                    This will permanently remove this {filter === 'images' ? 'image' : filter === 'audio' ? 'audio track' : 'video'} from your library.
                    Posts already scheduled with this media won't be affected.
                </Modal.Body>
                <Modal.Footer style={{ border: 'none' }}>
                    <button className="btn btn-sm sp-btn-ghost" style={{ border: '1px solid #d1d9e0', borderRadius: 8 }} onClick={() => setShowDeleteModal(false)}>Cancel</button>
                    <button className="btn btn-sm btn-danger" style={{ borderRadius: 8 }} onClick={handleDelete}>Delete</button>
                </Modal.Footer>
            </Modal>

            {/* ── Upload modal ────────────────────────────────────────────── */}
            <Modal show={showUploadModal} onHide={() => setShowUploadModal(false)} size="lg" centered>
                <Modal.Header closeButton style={{ border: 'none', paddingBottom: 0 }}>
                    <Modal.Title style={{ fontSize: '1rem', fontWeight: 500 }}>
                        Upload {filter === 'images' ? 'images' : filter === 'audio' ? 'audio' : 'videos'}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: 16 }}>
                        Files will be automatically added to your library once uploaded.
                    </p>
                    {filter === 'images' ? (
                        <ImageUploader uploadedImages={tempImages} setUploadedImages={setTempImages} />
                    ) : filter === 'audio' ? (
                        <AudioUploader uploadedAudio={tempAudio} setUploadedAudio={setTempAudio} />
                    ) : (
                        <VideoUploader uploadedVideos={tempVideos} setUploadedVideos={setTempVideos} />
                    )}
                </Modal.Body>
                <Modal.Footer style={{ border: 'none' }}>
                    <button className="btn btn-sm sp-btn-ghost" style={{ border: '1px solid #d1d9e0', borderRadius: 8 }} onClick={() => setShowUploadModal(false)}>Cancel</button>
                    <button className="btn btn-sm btn-primary" style={{ borderRadius: 8 }} onClick={handleUploadFinished}>Done & refresh</button>
                </Modal.Footer>
            </Modal>
        </MainStructure>
    );
}

export default MediaLibrary;
